import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import { useTranslation } from 'react-i18next'

import { AVAILABLE_COMMUNICATION_BOARD_SECTIONS } from '@/containers/SubscriptionFormPageNew/Components/CommunicationBoard/constants'

import { selectCommunicationBoardStoreData } from './communicationBoardSelectors'
import { setSection, setContent, reset } from './communicationBoardSlice'
import { communicationBoardActions } from './communicationBoardSagas'

const useCommunicationBoard = ({ sections = AVAILABLE_COMMUNICATION_BOARD_SECTIONS } = {}) => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useTranslation('devToolsNew')

  const {
    section: storedSection,
    content,
    isLoading,
    isSaving,
    resultError,
    resultMessage,
  } = useSelector(selectCommunicationBoardStoreData)
  const section = sections.includes(storedSection) ? storedSection : sections[0]
  // The content stays '' until the section's fetch answers: saving then would wipe the section.
  const isSaveDisabled = isLoading || isSaving

  useEffect(() => {
    dispatch(reset(section))
  }, [])

  useEffect(() => {
    dispatch({
      type: communicationBoardActions.FETCH,
      enqueue,
      t,
      payload: { section },
    })
  }, [section])

  const sectionsOptions = sections.reduce((acc, sectionKey) => {
    acc[sectionKey] = t(`communication_board.sections.${sectionKey}`)
    return acc
  }, {})

  const onSectionChange = id => dispatch(setSection(id ?? sections[0]))
  const onContentChange = value => dispatch(setContent(value))

  const onSave = () => {
    dispatch({
      type: communicationBoardActions.SAVE,
      enqueue,
      t,
      payload: { section },
    })
  }

  return {
    form: {
      sectionsOptions,
      section,
      content,
      isLoading,
      isSaving,
      isSaveDisabled,
      onSectionChange,
      onContentChange,
      onSave,
    },
    result: {
      error: resultError,
      message: resultMessage,
    },
    t,
  }
}

export default useCommunicationBoard
